import {
  Box,
  Flex,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Button,
  Input,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tag,
  Stack,
  Text,
  useColorModeValue,
  Icon,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Divider,
  Heading,
  Badge,
  Grid,
  GridItem,
  Checkbox,
  Select,
  useToast,
} from "@chakra-ui/react";
import { useState, useEffect, useCallback } from "react";
import { SearchIcon } from "@chakra-ui/icons";
import {
  FiActivity,
  FiClock,
  FiAlertTriangle,
  FiCheckCircle,
  FiFileText,
  FiPrinter,
  FiDownload,
  FiChevronLeft,
  FiChevronRight,
  FiMaximize2,
} from "react-icons/fi";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import ServiceHome from "../Home/ServiceHome";
import { buildServiceOrderFilterParams, getNextSortValue, SERVICE_ORDER_SORT_OPTIONS } from "../Os/osDateUtils";
import HomeSortableTh from "./HomeSortableTh";
import authService from "../../routes/authService";
import { useUser } from "../../components/Navbar/UserContext";
import ExecuteOsModal from "../OsExecution/ExecuteOsModal";
import { getExecutionByServiceOrderId } from "../OsExecution/osExecutionService";
import HomeDashboardView from "./HomeDashboardView";

// Função auxiliar para formatar a data como AAAA-MM-DD
const formatDateToYYYYMMDD = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export default function Home({ useModernLayout = false }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isExecuteOpen,
    onOpen: onExecuteOpen,
    onClose: onExecuteClose,
  } = useDisclosure();
  const [selectedOS, setSelectedOS] = useState(null);
  const [selectedOsExecution, setSelectedOsExecution] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const navigate = useNavigate();
  const { currentUser, isLoadingUser, userNameFromEmployees } = useUser();
  const toast = useToast();

  // Mova as chamadas useColorModeValue para o topo do componente
  const bgColorPage = useColorModeValue("gray.50", "gray.800");
  const bgColorCard = useColorModeValue("white", "gray.700");
  const bgColorBox = useColorModeValue("white", "gray.700");
  const bgColorTable = useColorModeValue("white", "gray.700");

  // Inicialize os filtros com o mês atual
  const [filters, setFilters] = useState(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth(); // Mês atual (0 a 11)

    // Primeiro dia do mês atual
    const firstDayOfMonth = new Date(year, month, 1);
    const formattedFirstDay = formatDateToYYYYMMDD(firstDayOfMonth);

    // Último dia do mês atual
    const lastDayOfMonth = new Date(year, month + 1, 0);
    const formattedLastDay = formatDateToYYYYMMDD(lastDayOfMonth);

    return {
      startDate: formattedFirstDay,
      endDate: formattedLastDay,
      priority: "",
      status: "",
      os: "",
    };
  });

  const [shouldFetch, setShouldFetch] = useState(false);
  const [sortBy, setSortBy] = useState(
    () => localStorage.getItem("homeOsSort") || "dateRequest,desc"
  );

  const [serviceOrders, setServiceOrders] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalElementsFromBackend, setTotalElementsFromBackend] = useState(0);
  const [totalPagesFromBackend, setTotalPagesFromBackend] = useState(0);

  // Estados para os contadores da API
  const [apiInProgressCount, setApiInProgressCount] = useState(0);
  const [apiDelayedCount, setApiDelayedCount] = useState(0);
  const [apiCompletedCount, setApiCompletedCount] = useState(0);

  // ESTADOS PARA OS MAPAS DE OPÇÕES (prioridade, status, equipe,
  //  tipo de serviço, endereço da empresa)
  const [priorityMap, setPriorityMap] = useState({});
  const [statusMap, setStatusMap] = useState({});
  const [teamMap, setTeamMap] = useState({});
  const [serviceTypeMap, setServiceTypeMap] = useState({});
  const [companyAddressMap, setCompanyAddressMap] = useState({});

  // Novo estado para a prioridade editada no modal
  const [editedPriorityId, setEditedPriorityId] = useState(null);

  // Função para "filtrar" os dados (agora apenas passa os dados do backend diretamente)
  const filterData = useCallback(() => {
    setFilteredData(serviceOrders);
  }, [serviceOrders]);

  const loadOptions = useCallback(async () => {
    try {
      const typesToFetch = [
        "priority",
        "statusofservice",
        "team",
        "typeofservice",
      ];
      const fetchedOptions = {};

      for (const type of typesToFetch) {
        try {
          const data = await ServiceHome.fetchOptionsByType(type);
          fetchedOptions[type] = data;
        } catch (fetchError) {
          console.error(
            `Falha ao buscar opções para o tipo ${type}:`,
            fetchError
          );
        }
      }

      const newPriorityMap = {};
      const newStatusMap = {};
      const newTeamMap = {};
      const newServiceTypeMap = {};

      if (fetchedOptions.priority) {
        fetchedOptions.priority.forEach((option) => {
          newPriorityMap[option.optionsId] = option.description;
        });
      }
      if (fetchedOptions.statusofservice) {
        fetchedOptions.statusofservice.forEach((option) => {
          newStatusMap[option.optionsId] = option.description;
        });
      }
      if (fetchedOptions.team) {
        fetchedOptions.team.forEach((option) => {
          newTeamMap[option.optionsId] = option.description;
        });
      }
      if (fetchedOptions.typeofservice) {
        fetchedOptions.typeofservice.forEach((option) => {
          newServiceTypeMap[option.optionsId] = option.description;
        });
      }

      setPriorityMap(newPriorityMap);
      setStatusMap(newStatusMap);
      setTeamMap(newTeamMap);
      setServiceTypeMap(newServiceTypeMap);

      const addresses = await ServiceHome.fetchAllCompanyAddresses();
      const newCompanyAddressMap = {};
      if (addresses) {
        addresses.forEach((address) => {
          newCompanyAddressMap[address.companyAddressId] = address;
        });
      }
      setCompanyAddressMap(newCompanyAddressMap);
    } catch (err) {
      console.error(
        "Erro geral ao carregar dados de configuração (opções ou endereços):",
        err
      );
    }
  }, []);

  const fetchServiceOrdersData = useCallback(
    async (page, size, currentFilters, currentSort) => {
      setLoading(true);
      setError(null);

      try {
        await loadOptions();

        const apiParams = buildServiceOrderFilterParams(
          currentFilters,
          page,
          size,
          currentSort
        );

        const data = await ServiceHome.fetchServiceOrders(apiParams);

        setServiceOrders(data.content || []);
        setTotalElementsFromBackend(data.totalElements || 0);
        setTotalPagesFromBackend(data.totalPages || 0);
      } catch (err) {
        console.error("Erro ao carregar dados:", err);
        setError("Não foi possível carregar os dados.");
        setServiceOrders([]);
        setTotalElementsFromBackend(0);
        setTotalPagesFromBackend(0);
      } finally {
        setLoading(false);
      }
    },
    [loadOptions]
  );

  useEffect(() => {
    if (!filters.startDate || !filters.endDate) {
      return;
    }
    fetchServiceOrdersData(currentPage, itemsPerPage, filters, sortBy);
  }, [fetchServiceOrdersData, currentPage, itemsPerPage, sortBy]);

  useEffect(() => {
    if (shouldFetch && filters.startDate && filters.endDate) {
      fetchServiceOrdersData(currentPage, itemsPerPage, filters, sortBy);
      setShouldFetch(false);
    }
  }, [shouldFetch, fetchServiceOrdersData, currentPage, itemsPerPage, filters, sortBy]);

  useEffect(() => {
    const fetchDashboardTotals = async () => {
      try {
        const totals = await ServiceHome.fetchServiceOrderTotals();

        const inProgress = totals["Em andamento"] || totals["Pendente"] || 0;
        const delayed = totals["Atrasado"] || 0;
        const completed =
          (totals["Concluida"] || 0) + (totals["Finalizada"] || 0);

        setApiInProgressCount(inProgress);
        setApiDelayedCount(delayed);
        setApiCompletedCount(completed);
      } catch (err) {
        console.error("Erro ao buscar totais do dashboard:", err);
        setApiInProgressCount(0);
        setApiDelayedCount(0);
        setApiCompletedCount(0);
      }
    };

    fetchDashboardTotals();
  }, []);

  useEffect(() => {
    filterData();
  }, [serviceOrders, filterData]);

  useEffect(() => {
    setSelectedRows([]);
  }, [filteredData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
  };

  const handleApplyFilters = () => {
    setCurrentPage(0);
    setShouldFetch(true);
  };

  const applySort = (value) => {
    setSortBy(value);
    localStorage.setItem("homeOsSort", value);
    setCurrentPage(0);
  };

  const handleSortChange = (e) => {
    applySort(e.target.value);
  };

  const handleColumnSort = (field, defaultDirection = "asc") => {
    applySort(getNextSortValue(sortBy, field, defaultDirection));
  };

  const getPriorityColor = (priorityId) => {
    const priorityName = priorityMap[priorityId]?.toLowerCase();
    switch (priorityName) {
      case "alta":
        return "red";
      case "média":
        return "orange";
      case "baixa":
        return "green";
      default:
        return "gray";
    }
  };

  const getStatusColor = (situation) => {
    const statusName = statusMap[situation]?.toLowerCase();
    switch (statusName) {
      case "cancelado":
        return "gray";
      case "pendente":
        return "yellow";
      case "em andamento":
        return "blue";
      case "finalizada":
      case "concluida":
        return "green";
      case "atrasado":
        return "red";
      default:
        return "gray";
    }
  };

  const handleOpenDetails = async (os) => {
    setSelectedOS(os);
    setEditedPriorityId(os.priorityId);
    setSelectedOsExecution(null);
    onOpen();

    try {
      const execution = await getExecutionByServiceOrderId(os.serviceOrderid);
      setSelectedOsExecution(execution);
    } catch (error) {
      console.error("Erro ao buscar execução da OS:", error);
    }
  };

  // Handler para mudança da prioridade no modal
  const handlePriorityChange = async (e) => {
    const newPriorityId = e.target.value;
    setEditedPriorityId(newPriorityId);

    if (selectedOS && newPriorityId) {
      try {
        const updatedOS = {
          ...selectedOS,
          priorityId: newPriorityId,

          dateRequest: selectedOS.dateRequest
            ? new Date(selectedOS.dateRequest).toISOString()
            : null,
          dateEnd: selectedOS.dateEnd
            ? new Date(selectedOS.dateEnd).toISOString()
            : null,
          dateStart: selectedOS.dateStart
            ? new Date(selectedOS.dateStart).toISOString()
            : null,
          dateExpected: selectedOS.dateExpected
            ? new Date(selectedOS.dateExpected).toISOString()
            : null,
        };

        await ServiceHome.updateServiceOrder(
          selectedOS.serviceOrderid,
          updatedOS
        );

        setServiceOrders((prevOrders) =>
          prevOrders.map((order) =>
            order.serviceOrderid === selectedOS.serviceOrderid
              ? updatedOS
              : order
          )
        );

        setSelectedOS(updatedOS);

        toast({
          title: "Prioridade atualizada.",
          description: "A prioridade da OS foi alterada com sucesso.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } catch (err) {
        console.error("Erro ao atualizar prioridade:", err);
        toast({
          title: "Erro ao atualizar prioridade.",
          description:
            "Não foi possível atualizar a prioridade da OS. Tente novamente.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    }
  };

  const SectionTitle = ({ children }) => (
    <Heading
      size="md"
      mt={6}
      mb={3}
      color="#C1272D"
      borderBottom="1px solid #E0E0E0"
      pb={1}
    >
      {children}
    </Heading>
  );

  const handleSelectAllRows = (event) => {
    if (event.target.checked) {
      setSelectedRows(filteredData.map((item) => item.serviceOrderid));
    } else {
      setSelectedRows([]);
    }
  };

  const handleSelectRow = (osId, event) => {
    if (event.target.checked) {
      setSelectedRows((prevSelectedRows) => [...prevSelectedRows, osId]);
    } else {
      setSelectedRows((prevSelectedRows) =>
        prevSelectedRows.filter((id) => id !== osId)
      );
    }
  };

  const isAllSelected =
    selectedRows.length === filteredData.length && filteredData.length > 0;
  const isAnySelected = selectedRows.length > 0;

  const generateOSDetailsHtml = (os) => {
    const getBadgeClass = (color) => {
      switch (color) {
        case "red":
          return "red";
        case "orange":
          return "orange";
        case "green":
          return "green";
        case "yellow":
          return "yellow";
        case "blue":
          return "blue";
        case "gray":
          return "gray";
        default:
          return "";
      }
    };

    const displayPriority =
      priorityMap[os.priorityId] || os.priorityId || "N/A";
    const displayStatus = statusMap[os.situation] || os.situation || "";
    const displayTeam = teamMap[os.teamId] || os.teamId || "N/A";
    const displayServiceType =
      serviceTypeMap[os.companyRequests?.optionsId] || os.summary || "N/A";

    const companyAddress = companyAddressMap[os.companyAddressId];
    const unitName = companyAddress?.unityName || "N/A";
    const floor = os.floor || "N/A";
    const complement1 = os.addressDetails || companyAddress?.additionalAddressDetails || "N/A";
    const complement2 = os.addressDetails2 || "N/A";
    const companyName = companyAddress?.company?.description || "N/A";

    return `
      <div class="os-card">
          <h2 class="os-card-title">OS: ${
            os.orderNumber || "N/A"
          } - ${displayServiceType}</h2>

          <h3 class="section-heading">Informações Gerais</h3>
          <div class="grid grid-2-cols">
              <p><strong>Número OS:</strong> ${os.orderNumber || "N/A"}</p>
              <p><strong>Serviço:</strong> ${displayServiceType}</p>
              <p><strong>Prioridade:</strong> <span class="badge ${getBadgeClass(
                getPriorityColor(os.priorityId || "")
              )}">${displayPriority}</span></p>
              <p><strong>Situação:</strong> <span class="badge ${getBadgeClass(
                getStatusColor(os.situation || "")
              )}">${displayStatus}</span></p>
              <p><strong>Data de Criação:</strong> ${
                new Date(os.dateRequest).toLocaleDateString("pt-BR") || "N/A"
              }</p>
              <p><strong>Início Efetivo:</strong> ${
                new Date(os.dateStart).toLocaleDateString("pt-BR") || "N/A"
              }</p>
              <p><strong>Término Previsto (SLA):</strong> ${
                new Date(os.dateEnd).toLocaleDateString("pt-BR") || "N/A"
              }</p>
              <p class="full-width"><strong>Agendamento:</strong> ${
                new Date(os.dateExpected).toLocaleDateString("pt-BR") || "N/A"
              }</p>
          </div>

          <h3 class="section-heading">Solicitante</h3>
          <div class="grid grid-2-cols">
              <p><strong>Nome:</strong> ${os.requesterName || "N/A"}</p>
              <p><strong>Telefone:</strong> ${
                os.requestClientPhone || "N/A"
              }</p>
              <p class="full-width"><strong>Email:</strong> ${
                os.requestClientMail || "N/A"
              }</p>
          </div>

          <h3 class="section-heading">Solicitado Para</h3>
          <div class="grid grid-2-cols">
              <p><strong>Nome:</strong> ${os.recipientClientName || "N/A"}</p>
              <p><strong>Telefone:</strong> ${
                os.recipientClientPhone || "N/A"
              }</p>
              <p class="full-width"><strong>Email:</strong> ${
                os.recipientClientMail || "N/A"
              }</p>
          </div>

          <h3 class="section-heading">Local do Serviço</h3>
          <div class="grid grid-2-cols">
              <p class="full-width"><strong>Empresa:</strong> ${companyName}</p>
              <p class="full-width"><strong>Endereço:</strong> ${complement1}</p>
              <p><strong>Unidade:</strong> ${unitName}</p>
              <p><strong>Andar:</strong> ${floor}</p>
              <p><strong>Complemento 1:</strong> ${complement1}</p>
              <p><strong>Complemento 2:</strong> ${complement2}</p>
          </div>

          <h3 class="section-heading">Detalhes</h3>
          <div class="text-block">
              <p><strong>Resumo do Serviço:</strong></p>
              <div class="content-box">${os.summary || "N/A"}</div>
          </div>
          <div class="text-block">
              <p><strong>Descrição do Serviço:</strong></p>
              <div class="content-box">${os.details || "N/A"}</div>
          </div>

          ${
            os.materials && os.materials.length > 0
              ? `
          <div class="text-block">
              <p><strong>Materiais Utilizados:</strong></p>
              <div class="content-box">${os.materials.join(", ")}</div>
          </div>`
              : ""
          }

          ${
            os.notes
              ? `
          <div class="text-block">
              <p><strong>Observações:</strong></p>
              <div class="content-box">${os.notes}</div>
          </div>`
              : ""
          }
      </div>
      <div class="page-break"></div>
    `;
  };

  const handleExportToExcel = () => {
    if (selectedRows.length === 0) {
      alert("Por favor, selecione ao menos uma OS para exportar.");
      return;
    }

    const osToExport = serviceOrders.filter((item) =>
      selectedRows.includes(item.serviceOrderid)
    );

    const dataToExport = osToExport.map((os) => {
      const companyAddress = companyAddressMap[os.companyAddressId];
      const unitName = companyAddress?.unityName || "";
      const floor = os.floor || "";
      const complement1 = os.addressDetails || companyAddress?.additionalAddressDetails || "";
      const complement2 = os.addressDetails2 || "";
      const companyName = companyAddress?.company?.description || "";

      return {
        "ID da OS (API)": os.serviceOrderid || "",
        "Número OS": os.orderNumber || "",
        Solicitante: os.requesterName || "",
        "Telefone Solicitante": os.requestClientPhone || "",
        "Email Solicitante": os.requestClientMail || "",
        "Solicitado Para Nome": os.recipientClientName || "",
        "Solicitado Para Telefone": os.recipientClientPhone || "",
        "Solicitado Para Email": os.recipientClientMail || "",
        "Serviço (Resumo)":
          serviceTypeMap[os.companyRequests?.optionsId] || os.summary || "",
        "Data Agendada (Prevista)":
          new Date(os.dateExpected).toLocaleDateString("pt-BR") || "",
        Prioridade: priorityMap[os.priorityId] || os.priorityId || "",
        Situação: statusMap[os.situation] || os.situation || "",
        "Resumo do Serviço": os.summary || "",
        "Descrição Detalhada": os.details || "",
        "Data de Criação":
          new Date(os.dateRequest).toLocaleDateString("pt-BR") || "",
        "Início Efetivo":
          new Date(os.dateStart).toLocaleDateString("pt-BR") || "",
        "Término Previsto (SLA)":
          new Date(os.dateEnd).toLocaleDateString("pt-BR") || "",
        "Empresa Associada": companyName,
        Unidade: unitName,
        Andar: floor,
        "Complemento Endereço 1": complement1,
        "Complemento Endereço 2": complement2,
        "Empresa (direto da OS, se houver)": os.company || "",
        "Local (direto da OS, se houver)": os.location || "",
        "Materiais Utilizados": os.materials?.join(", ") || "",
        Observações: os.notes || "",
      };
    });

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Ordens de Serviço");
    XLSX.writeFile(wb, "ordens_de_servico.xlsx");
  };

  const handlePrintSelected = () => {
    if (selectedRows.length === 0) {
      alert("Por favor, selecione ao menos uma OS para imprimir.");
      return;
    }

    const osToPrint = serviceOrders.filter((item) =>
      selectedRows.includes(item.serviceOrderid)
    );

    let allOsDetailsHtml = "";
    osToPrint.forEach((os) => {
      allOsDetailsHtml += generateOSDetailsHtml(os);
    });

    allOsDetailsHtml = allOsDetailsHtml.replace(
      /<div class="page-break"><\/div>$/,
      ""
    );

    const printStyles = `
      @page { size: A4 portrait; margin: 1cm; }
      body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; margin: 0; padding: 0; color: #333; font-size: 10pt; }
      h1 { color: #C1272D; text-align: center; margin-bottom: 20px; font-size: 1.8em; }
      .os-card { border: 1px solid #ddd; padding: 15px; margin-bottom: 25px; border-radius: 8px; background-color: #fff; box-shadow: 0 2px 4px rgba(0,0,0,0.05); page-break-inside: avoid; }
      .os-card-title { color: #C1272D; font-size: 1.5em; margin-top: 0; margin-bottom: 15px; padding-bottom: 8px; border-bottom: 1px solid #C1272D; text-align: center; }
      .section-heading { font-size: 1.1em; color: #625752; border-bottom: 1px dashed #E0E0E0; padding-bottom: 3px; margin-top: 20px; margin-bottom: 10px; }
      .grid { display: grid; gap: 5px 15px; margin-bottom: 10px; }
      .grid-2-cols { grid-template-columns: repeat(2, 1fr); }
      .grid p { margin: 0; line-height: 1.4; font-size: 0.95em; }
      .grid .full-width { grid-column: span 2; }
      .text-block p { margin: 0 0 5px 0; font-size: 0.95em;}
      .content-box { background-color: #f8f8f8; border: 1px solid #eee; padding: 10px; border-radius: 6px; white-space: pre-wrap; font-size: 0.9em; }
      .badge { display: inline-block; padding: 3px 8px; border-radius: 4px; font-size: 0.8em; font-weight: bold; color: white; text-transform: uppercase; vertical-align: middle; }
      .badge.red { background-color: #C1272D; }
      .badge.orange { background-color: #dd6b20; }
      .badge.green { background-color: #38a169; }
      .badge.yellow { background-color: #d69e2e; color: #333; }
      .badge.blue { background-color: #3182ce; }
      .badge.gray { background-color: #a0aec0; }
      @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } .page-break { page-break-after: always; } .os-card { box-shadow: none !important; } }
    `;

    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
      <head>
          <title>Detalhes Completos da OS</title>
          <style>${printStyles}</style>
      </head>
      <body>
          <h1>Relatório Detalhado de Ordens de Serviço</h1>
          ${allOsDetailsHtml}
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const canEditOs = authService.hasAnyRole(["ROLE_ADMIN", "ROLE_ENCARREGADO"]);
  const isUsuario = authService.hasAnyRole(["ROLE_USUARIO", "USUARIO"]);

  const handleOpenExecuteModal = () => {
    onExecuteOpen();
  };

  const handleExecutionSaved = (execution) => {
    setSelectedOsExecution(execution);

    const finalizedStatusId = Object.entries(statusMap).find(
      ([, label]) => label?.toLowerCase() === "finalizada"
    )?.[0];

    if (finalizedStatusId && selectedOS?.serviceOrderid) {
      const updateSituation = (os) =>
        os.serviceOrderid === selectedOS.serviceOrderid
          ? { ...os, situation: finalizedStatusId }
          : os;

      setSelectedOS((prev) => (prev ? updateSituation(prev) : prev));
      setServiceOrders((prev) => prev.map(updateSituation));
    }
  };

  const hasExecution = Boolean(selectedOsExecution?.publicToken);
  const canUploadExecution = Boolean(currentUser);

  const dashboardContent = useModernLayout ? (
    <HomeDashboardView
      userName={userNameFromEmployees || currentUser?.name}
      apiInProgressCount={apiInProgressCount}
      apiDelayedCount={apiDelayedCount}
      apiCompletedCount={apiCompletedCount}
      filters={filters}
      handleInputChange={handleInputChange}
      handleApplyFilters={handleApplyFilters}
      sortBy={sortBy}
      handleSortChange={handleSortChange}
      handleColumnSort={handleColumnSort}
      sortOptions={SERVICE_ORDER_SORT_OPTIONS}
      priorityMap={priorityMap}
      statusMap={statusMap}
      loading={loading}
      error={error}
      filteredData={filteredData}
      serviceTypeMap={serviceTypeMap}
      companyAddressMap={companyAddressMap}
      selectedRows={selectedRows}
      handleSelectAllRows={handleSelectAllRows}
      handleSelectRow={handleSelectRow}
      isAllSelected={isAllSelected}
      isAnySelected={isAnySelected}
      getPriorityColor={getPriorityColor}
      getStatusColor={getStatusColor}
      handleOpenDetails={handleOpenDetails}
      handlePrintSelected={handlePrintSelected}
      handleExportToExcel={handleExportToExcel}
      currentPage={currentPage}
      setCurrentPage={setCurrentPage}
      itemsPerPage={itemsPerPage}
      setItemsPerPage={setItemsPerPage}
      totalElementsFromBackend={totalElementsFromBackend}
      totalPagesFromBackend={totalPagesFromBackend}
    />
  ) : (
    <>
      <Flex alignItems="center" gap={2} mb={6}>
        <Icon as={FiActivity} boxSize={6} color="blue.500" />
        <Text fontSize="2xl" fontWeight="bold">
          Dashboard de OS
        </Text>
      </Flex>

      <SimpleGrid columns={{ base: 1, md: 4 }} spacing={6} mb={8}>
        <Stat p={4} shadow="sm" bg={bgColorCard} rounded="md">
          <StatLabel>Total de OS</StatLabel>
          <StatNumber>
            {apiInProgressCount + apiDelayedCount + apiCompletedCount}
          </StatNumber>
          <StatHelpText>Total de registros na API</StatHelpText>
        </Stat>
        <Stat p={4} shadow="sm" bg={bgColorCard} rounded="md">
          <StatLabel>Em Andamento</StatLabel>{" "}
          <StatNumber>{apiInProgressCount}</StatNumber>
          <StatHelpText>
            <Icon as={FiClock} mr={1} /> Dados da API
          </StatHelpText>
        </Stat>
        <Stat p={4} shadow="sm" bg={bgColorCard} rounded="md">
          <StatLabel>Atrasadas</StatLabel>{" "}
          <StatNumber color="red.500">{apiDelayedCount}</StatNumber>
          <StatHelpText>
            <Icon as={FiAlertTriangle} mr={1} /> Dados da API
          </StatHelpText>
        </Stat>
        <Stat p={4} shadow="sm" bg={bgColorCard} rounded="md">
          <StatLabel>Concluídas</StatLabel>{" "}
          <StatNumber color="green.500">{apiCompletedCount}</StatNumber>
          <StatHelpText>
            <Icon as={FiCheckCircle} mr={1} /> Dados da API
          </StatHelpText>
        </Stat>
      </SimpleGrid>

      <Stack
        direction={{ base: "column", md: "row" }}
        spacing={4}
        mb={6}
        p={4}
        bg={bgColorBox}
        rounded="md"
        shadow="sm"
      >
        <Input
          type="date"
          name="startDate"
          value={filters.startDate}
          onChange={handleInputChange}
          flex="1"
        />
        <Input
          type="date"
          name="endDate"
          value={filters.endDate}
          onChange={handleInputChange}
          flex="1"
        />

        <Select
          name="priority"
          placeholder="Selecione a Prioridade"
          value={filters.priority}
          onChange={handleInputChange}
          flex="1"
        >
          <option value="">Todas</option>
          {Object.entries(priorityMap).map(([id, description]) => (
            <option key={id} value={id}>
              {description}
            </option>
          ))}
        </Select>

        <Select
          name="status"
          placeholder="Selecione a Situação"
          value={filters.status}
          onChange={handleInputChange}
          flex="1"
        >
          <option value="">Todas</option>
          {Object.entries(statusMap).map(([id, description]) => (
            <option key={id} value={id}>
              {description}
            </option>
          ))}
        </Select>

        <Input
          name="os"
          placeholder="Número da OS"
          value={filters.os}
          onChange={handleInputChange}
          flex="1"
        />
        <Select
          value={sortBy}
          onChange={handleSortChange}
          flex="1"
        >
          {SERVICE_ORDER_SORT_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
        <Button
          colorScheme="blue"
          leftIcon={<SearchIcon />}
          onClick={handleApplyFilters}
          flexShrink={0}
        >
          Buscar
        </Button>
      </Stack>

      <Flex mb={4} justify="flex-end" gap={3}>
        <Button
          colorScheme="teal"
          leftIcon={<FiPrinter />}
          onClick={handlePrintSelected}
          isDisabled={!isAnySelected}
        >
          Imprimir Detalhes Selecionados
        </Button>
        <Button
          colorScheme="green"
          leftIcon={<FiDownload />}
          onClick={handleExportToExcel}
          isDisabled={!isAnySelected}
        >
          Exportar para Excel
        </Button>
      </Flex>

      {loading && (
        <Text textAlign="center" py={4}>
          Carregando ordens de serviço...
        </Text>
      )}
      {error && (
        <Text textAlign="center" py={4} color="red.500">
          {error}
        </Text>
      )}

      {!loading && !error && (
        <Box overflowX="auto" bg={bgColorTable} p={4} rounded="md" shadow="sm">
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>
                  <Checkbox
                    isChecked={isAllSelected}
                    onChange={handleSelectAllRows}
                    isDisabled={filteredData.length === 0}
                  />
                </Th>
                <HomeSortableTh
                  field="orderNumber"
                  sortBy={sortBy}
                  onColumnSort={handleColumnSort}
                  defaultDirection="desc"
                >
                  Número OS
                </HomeSortableTh>
                <HomeSortableTh
                  field="requesterName"
                  sortBy={sortBy}
                  onColumnSort={handleColumnSort}
                >
                  Solicitante
                </HomeSortableTh>
                <HomeSortableTh
                  field="summary"
                  sortBy={sortBy}
                  onColumnSort={handleColumnSort}
                >
                  Serviço
                </HomeSortableTh>
                <HomeSortableTh
                  field="dateExpected"
                  sortBy={sortBy}
                  onColumnSort={handleColumnSort}
                >
                  Agendamento
                </HomeSortableTh>
                <HomeSortableTh
                  field="priorityId"
                  sortBy={sortBy}
                  onColumnSort={handleColumnSort}
                  defaultDirection="desc"
                >
                  Prioridade
                </HomeSortableTh>
                <HomeSortableTh
                  field="situationId"
                  sortBy={sortBy}
                  onColumnSort={handleColumnSort}
                >
                  Situação
                </HomeSortableTh>
                <HomeSortableTh
                  field="unityName"
                  sortBy={sortBy}
                  onColumnSort={handleColumnSort}
                >
                  Unidade
                </HomeSortableTh>
                <Th>Ações</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredData.map((item) => (
                <Tr key={item.serviceOrderid}>
                  <Td>
                    <Checkbox
                      isChecked={selectedRows.includes(item.serviceOrderid)}
                      onChange={(e) => handleSelectRow(item.serviceOrderid, e)}
                    />
                  </Td>
                  <Td fontWeight="bold">{item.orderNumber || "N/A"}</Td>
                  <Td>{item.requesterName || "N/A"}</Td>
                  <Td>
                    {serviceTypeMap[item.companyRequests?.optionsId] ||
                      item.summary ||
                      "N/A"}
                  </Td>
                  <Td>
                    {new Date(item.dateExpected).toLocaleDateString("pt-BR") ||
                      "N/A"}
                  </Td>
                  <Td>
                    <Tag colorScheme={getPriorityColor(item.priorityId || "")}>
                      {priorityMap[item.priorityId] || "N/A"}
                    </Tag>
                  </Td>
                  <Td>
                    <Tag colorScheme={getStatusColor(item.situation || "")}>
                      {statusMap[item.situation] || "N/A"}
                    </Tag>
                  </Td>
                  <Td>
                    {companyAddressMap[item.companyAddressId]?.unityName ||
                      "N/A"}
                  </Td>
                  <Td>
                    <Button
                      size="sm"
                      colorScheme="teal"
                      variant="outline"
                      onClick={() => handleOpenDetails(item)}
                    >
                      Detalhes
                    </Button>
                  </Td>
                </Tr>
              ))}
              {filteredData.length === 0 && (
                <Tr>
                  <Td colSpan={9} textAlign="center">
                    Nenhuma Ordem de Serviço encontrada.
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </Box>
      )}

      {!loading && !error && totalElementsFromBackend > 0 && (
        <Flex
          justify="center"
          mt={6}
          align="center"
          direction={{ base: "column", md: "row" }}
          gap={4}
        >
          <Button
            onClick={() => setCurrentPage((prev) => Math.max(0, prev - 1))}
            isDisabled={currentPage === 0}
            leftIcon={<FiChevronLeft />}
            colorScheme="gray"
            variant="outline"
          >
            Anterior
          </Button>

          <Flex gap={1} flexWrap="wrap" justify="center">
            {Array.from({ length: totalPagesFromBackend }, (_, i) => i).map(
              (pageNumber) =>
                (pageNumber === 0 ||
                  pageNumber === totalPagesFromBackend - 1 ||
                  (pageNumber >= currentPage - 2 &&
                    pageNumber <= currentPage + 2)) && (
                  <Button
                    key={pageNumber}
                    onClick={() => setCurrentPage(pageNumber)}
                    isActive={currentPage === pageNumber}
                    colorScheme={currentPage === pageNumber ? "blue" : "gray"}
                    variant={currentPage === pageNumber ? "solid" : "outline"}
                    size="sm"
                  >
                    {pageNumber + 1}
                  </Button>
                )
            )}
          </Flex>

          <Button
            onClick={() =>
              setCurrentPage((prev) =>
                Math.min(totalPagesFromBackend - 1, prev + 1)
              )
            }
            isDisabled={
              currentPage === totalPagesFromBackend - 1 ||
              totalPagesFromBackend === 0
            }
            rightIcon={<FiChevronRight />}
            colorScheme="gray"
            variant="outline"
          >
            Próxima
          </Button>

          <Text fontSize="md" fontWeight="medium" ml={{ base: 0, md: 4 }}>
            Página {currentPage + 1} de {totalPagesFromBackend} (
            {totalElementsFromBackend} itens)
          </Text>

          <Select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(0);
            }}
            width="150px"
            ml={{ base: 0, md: 4 }}
          >
            <option value={5}>5 por página</option>
            <option value={10}>10 por página</option>
            <option value={20}>20 por página</option>
            <option value={50}>50 por página</option>
          </Select>
        </Flex>
      )}
    </>
  );

  return (
    <Box
      p={useModernLayout ? 0 : 8}
      bg={useModernLayout ? "transparent" : bgColorPage}
      minH="100vh"
      w="100%"
      maxW="100%"
    >
      {dashboardContent}

      {/* Modal de Detalhes  */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent borderRadius="xl" boxShadow="2xl">
          <ModalHeader
            bg="#C1272D"
            color="white"
            borderTopRadius="xl"
            display="flex"
            alignItems="center"
            gap={2}
          >
            <Icon as={FiFileText} /> Detalhes da Ordem de Serviço
          </ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody p={6}>
            {selectedOS && (
              <Box>
                <SectionTitle>Informações Gerais</SectionTitle>
                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <GridItem>
                    <Text>
                      <strong>Número OS:</strong>{" "}
                      {selectedOS.orderNumber || "N/A"}
                    </Text>
                  </GridItem>
                  <GridItem>
                    <Text>
                      <strong>Serviço:</strong>{" "}
                      {serviceTypeMap[selectedOS.companyRequests?.optionsId] ||
                        selectedOS.summary ||
                        "N/A"}
                    </Text>
                  </GridItem>
                  <GridItem>
                    <Text>
                      <strong>Prioridade:</strong>{" "}
                      {isUsuario ? (
                        <Select
                          value={editedPriorityId || ""}
                          onChange={handlePriorityChange}
                          width="auto"
                          display="inline-flex"
                          ml={2}
                          size="sm"
                        >
                          {Object.entries(priorityMap).map(
                            ([id, description]) => (
                              <option key={id} value={id}>
                                {description}
                              </option>
                            )
                          )}
                        </Select>
                      ) : (
                        <Badge
                          colorScheme={getPriorityColor(
                            selectedOS.priorityId || ""
                          )}
                        >
                          {priorityMap[selectedOS.priorityId] || "N/A"}
                        </Badge>
                      )}
                    </Text>
                  </GridItem>
                  <GridItem>
                    <Text>
                      <strong>Situação:</strong>{" "}
                      <Badge
                        colorScheme={getStatusColor(selectedOS.situation || "")}
                      >
                        {statusMap[selectedOS.situation] || "N/A"}
                      </Badge>
                      {hasExecution && (
                        <Badge ml={2} colorScheme="purple">
                          QR Code disponível
                        </Badge>
                      )}
                    </Text>
                  </GridItem>
                  <GridItem>
                    <Text>
                      <strong>Data de Criação:</strong>{" "}
                      {new Date(selectedOS.dateRequest).toLocaleDateString(
                        "pt-BR"
                      ) || "N/A"}
                    </Text>
                  </GridItem>
                  <GridItem>
                    <Text>
                      <strong>Início Efetivo:</strong>{" "}
                      {new Date(selectedOS.dateStart).toLocaleDateString(
                        "pt-BR"
                      ) || "N/A"}
                    </Text>
                  </GridItem>
                  <GridItem colSpan={2}>
                    <Text>
                      <strong>Término Previsto (SLA):</strong>{" "}
                      {new Date(selectedOS.dateEnd).toLocaleDateString(
                        "pt-BR"
                      ) || "N/A"}
                    </Text>
                  </GridItem>
                  <GridItem colSpan={2}>
                    <Text>
                      <strong>Agendamento:</strong>{" "}
                      {new Date(selectedOS.dateExpected).toLocaleDateString(
                        "pt-BR"
                      ) || "N/A"}
                    </Text>
                  </GridItem>
                </Grid>

                <SectionTitle>Solicitante</SectionTitle>
                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <GridItem>
                    <Text>
                      <strong>Nome:</strong> {selectedOS.requesterName || "N/A"}
                    </Text>
                  </GridItem>
                  <GridItem>
                    <Text>
                      <strong>Telefone:</strong>{" "}
                      {selectedOS.requestClientPhone || "N/A"}
                    </Text>
                  </GridItem>
                  <GridItem colSpan={2}>
                    <Text>
                      <strong>Email:</strong>{" "}
                      {selectedOS.requestClientMail || "N/A"}
                    </Text>
                  </GridItem>
                </Grid>

                <SectionTitle>Solicitado Para</SectionTitle>
                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <GridItem>
                    <Text>
                      <strong>Nome:</strong>{" "}
                      {selectedOS.recipientClientName || "N/A"}
                    </Text>
                  </GridItem>
                  <GridItem>
                    <Text>
                      <strong>Telefone:</strong>{" "}
                      {selectedOS.recipientClientPhone || "N/A"}
                    </Text>
                  </GridItem>
                  <GridItem colSpan={2}>
                    <Text>
                      <strong>Email:</strong>{" "}
                      {selectedOS.recipientClientMail || "N/A"}
                    </Text>
                  </GridItem>
                </Grid>

                <SectionTitle>Local do Serviço</SectionTitle>
                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <GridItem colSpan={2}>
                    <Text>
                      <strong>Empresa:</strong>{" "}
                      {companyAddressMap[selectedOS.companyAddressId]?.company
                        ?.description || "N/A"}
                    </Text>
                  </GridItem>
                  <GridItem colSpan={2}>
                    <Text>
                      <strong>Endereço:</strong>{" "}
                      {companyAddressMap[selectedOS.companyAddressId]
                        ?.additionalAddressDetails || "N/A"}
                    </Text>
                  </GridItem>

                  <GridItem>
                    <Text>
                      <strong>Unidade:</strong>{" "}
                      {companyAddressMap[selectedOS.companyAddressId]
                        ?.unityName || "N/A"}
                    </Text>
                  </GridItem>
                  {/* Adicione os novos campos aqui */}
                  {selectedOS.floor && (
                    <GridItem>
                      <Text>
                        <strong>Andar:</strong> {selectedOS.floor}
                      </Text>
                    </GridItem>
                  )}
                  {selectedOS.addressDetails && (
                    <GridItem>
                      <Text>
                        <strong>Complemento 1:</strong>{" "}
                        {selectedOS.addressDetails}
                      </Text>
                    </GridItem>
                  )}
                  {selectedOS.addressDetails2 && (
                    <GridItem>
                      <Text>
                        <strong>Complemento 2:</strong>{" "}
                        {selectedOS.addressDetails2}
                      </Text>
                    </GridItem>
                  )}
                </Grid>

                <SectionTitle>Detalhes</SectionTitle>
                <Text mt={4} mb={2}>
                  <strong>Resumo do Serviço:</strong>
                </Text>
                <Box
                  p={3}
                  bg="gray.50"
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor="gray.200"
                >
                  <Text>{selectedOS.summary || "N/A"}</Text>
                </Box>
                <Text mt={4} mb={2}>
                  <strong>Descrição do Serviço:</strong>
                </Text>
                <Box
                  p={3}
                  bg="gray.50"
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor="gray.200"
                >
                  <Text>{selectedOS.details || "N/A"}</Text>
                </Box>

                {selectedOS.materials && selectedOS.materials.length > 0 && (
                  <>
                    <Text mt={4} mb={2}>
                      <strong>Materiais Utilizados:</strong>
                    </Text>
                    <Box
                      p={3}
                      bg="gray.50"
                      borderRadius="md"
                      borderWidth="1px"
                      borderColor="gray.200"
                    >
                      <Text>{selectedOS.materials.join(", ")}</Text>
                    </Box>
                  </>
                )}

                {selectedOS.notes && (
                  <>
                    <Text mt={4} mb={2}>
                      <strong>Observações:</strong>
                    </Text>
                    <Box
                      p={3}
                      bg="gray.50"
                      borderRadius="md"
                      borderWidth="1px"
                      borderColor="gray.200"
                    >
                      <Text>{selectedOS.notes}</Text>
                    </Box>
                  </>
                )}
              </Box>
            )}
          </ModalBody>
          <ModalFooter>
            {selectedOS && (
              <Button
                leftIcon={<Icon as={FiMaximize2} />}
                variant="outline"
                borderColor="#C1272D"
                color="#C1272D"
                _hover={{ bg: "red.50" }}
                px={6}
                mr={3}
                onClick={handleOpenExecuteModal}
              >
                {hasExecution
                  ? "Ver execução / QR / Fotos"
                  : "Executar OS / Fotos / QR Code"}
              </Button>
            )}
            {selectedOS && canEditOs && (
              <Button
                bg="blue.500"
                color="white"
                _hover={{ bg: "blue.600" }}
                px={6}
                mr={3}
                onClick={() => {
                  onClose();
                  if (selectedOS?.serviceOrderid) {
                    navigate(`/os/editar/${selectedOS.serviceOrderid}`);
                  } else {
                    console.error(
                      "ID da OS não encontrado em selectedOS. Não foi possível navegar para edição."
                    );
                  }
                }}
              >
                Editar OS
              </Button>
            )}
            <Button
              bg="#C1272D"
              color="white"
              _hover={{ bg: "#a81f23" }}
              px={6}
              onClick={onClose}
            >
              Fechar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ExecuteOsModal
        isOpen={isExecuteOpen}
        onClose={onExecuteClose}
        serviceOrder={selectedOS}
        executedBy={{
          login: currentUser?.login,
          name: userNameFromEmployees || currentUser?.name,
        }}
        canEdit={canUploadExecution}
        initialExecution={selectedOsExecution}
        onSaved={handleExecutionSaved}
      />
    </Box>
  );
}
